import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../Firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { toast } from "react-toastify";
import "../styles/TeacherCourse.css";

const TeacherCourse = () => {
  const [courses, setCourses] = useState([]);
  const [videoMeta, setVideoMeta] = useState({});
  const [purchasedMap, setPurchasedMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [videoLoadingId, setVideoLoadingId] = useState(null); // 👈 NEW

  const navigate = useNavigate();
  const courseUnsubs = useRef([]);

  /* ================= AUTH + LOAD COURSES ================= */
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const q = query(
          collection(db, "courses"),
          where("instructorId", "==", user.uid)
        );

        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }));
        setCourses(list);

        /* PURCHASE CHECK */
        const ordersSnap = await getDocs(collection(db, "orders"));
        const pMap = {};
        ordersSnap.forEach((o) => {
          pMap[o.data().courseId] = true;
        });
        setPurchasedMap(pMap);

      } catch (err) {
        console.error(err);
        toast.error("Failed to load courses");
      } finally {
        setLoading(false);
      }
    });

    return () => unsubAuth();
  }, [navigate]);

  /* ================= REALTIME VIDEO META ================= */
  useEffect(() => {
    courseUnsubs.current.forEach((u) => u());
    courseUnsubs.current = [];

    courses.forEach((course) => {
      const unsub = onSnapshot(doc(db, "courses", course.id), (snap) => {
        const data = snap.data();
        const videos = data?.videos || [];

        const hasSample = videos.some(v => v.isSample === true);
        const hasPaid = videos.some(v => v.isSample === false);

        setVideoMeta((prev) => ({
          ...prev,
          [course.id]: {
            hasAnyVideo: videos.length > 0,
            hasSample,
            hasPaid
          }
        }));
      });

      courseUnsubs.current.push(unsub);
    });

    return () => {
      courseUnsubs.current.forEach((u) => u());
      courseUnsubs.current = [];
    };
  }, [courses]);

  /* ================= AUDIT LOG ================= */
  const logAudit = async (courseId, action) => {
    try {
      await addDoc(collection(db, "courseAuditLogs"), {
        courseId,
        teacherId: auth.currentUser.uid,
        action,
        timestamp: serverTimestamp()
      });
    } catch (_) {}
  };

  /* ================= DELETE ================= */
  const deleteCourse = async (courseId) => {
    if (!window.confirm("Delete this course permanently?")) return;

    try {
      await deleteDoc(doc(db, "courses", courseId));
      await logAudit(courseId, "DELETE");
      setCourses((prev) => prev.filter((c) => c.id !== courseId));
      toast.success("Course deleted successfully");
    } catch {
      toast.error("Failed to delete course");
    }
  };

  /* ================= POST / UNPOST ================= */
  const togglePublish = async (course) => {
    const meta = videoMeta[course.id];

    if (!course.isPublished) {
      if (!meta?.hasSample || !meta?.hasPaid) {
        toast.warning(
          "Add at least one sample video and one full video before posting."
        );
        return;
      }
    }

    try {
      await updateDoc(doc(db, "courses", course.id), {
        isPublished: !course.isPublished
      });

      await logAudit(
        course.id,
        course.isPublished ? "UNPOST" : "POST"
      );

      setCourses((prev) =>
        prev.map((c) =>
          c.id === course.id
            ? { ...c, isPublished: !course.isPublished }
            : c
        )
      );

      toast.success(
        course.isPublished
          ? "Course unpublished successfully"
          : "Course published successfully"
      );
    } catch {
      toast.error("Failed to update course status");
    }
  };

  /* ================= EDIT VIDEO CLICK ================= */
  const handleEditVideo = (courseId) => {
    setVideoLoadingId(courseId);
    navigate(`/teacherCourse/editVideo/${courseId}`);
  };

  if (loading) return <p style={{ padding: 40 }}>Loading...</p>;

  return (
    <div className="teacher-course-page">
      <div className="teacher-course-header">
        <h1>My Courses</h1>
        <button
          className="add-course-btn"
          onClick={() => navigate("/addCourse")}
        >
          + Add new course
        </button>
      </div>

      {courses.length === 0 && (
        <div className="no-course">You did not add any course</div>
      )}

      <div className="teacher-course-grid">
        {courses.map((course) => {
          const meta = videoMeta[course.id];
          const isPurchased = purchasedMap[course.id];

         
          return (
            <div className="teacher-course-card" key={course.id}>
              {/* STATUS BADGE */}
             
              {/* THUMBNAIL CLICK */}
              <img
                src={course.thumbnail || "/images/course-placeholder.png"}
                alt={course.title}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(`/teacherCourse/details/${course.id}`)
                }
              />

              <h3>{course.title}</h3>

              {/* PRICE */}
              <p className="price">
                {course.isFree ? (
                  <span className="discounted free">FREE</span>
                ) : course.discountPercent > 0 ? (
                  <>
                    <span className="original">₹{course.price}</span>
                    <span className="discounted">
                      ₹{course.discountedPrice}
                    </span>
                  </>
                ) : (
                  <span className="discounted">₹{course.price}</span>
                )}
              </p>

              {/* ACTIONS */}
              <button
                onClick={() =>
                  navigate(`/teacherCourse/editCourse/${course.id}`)
                }
              >
                Edit Course
              </button>

              {meta?.hasAnyVideo ? (
                <button
                className="delete"
                disabled={isPurchased}
                title={
                  isPurchased
                    ? "Students have purchased this course"
                    : "Edit Videos"
                }
                  onClick={() => handleEditVideo(course.id)}
                >
                  {videoLoadingId === course.id
                    ? "Loading..."
                    : "Edit Videos"}
                </button>
              ) : (
                <button
                  onClick={() =>
                    navigate(`/teacherCourse/uploadvideo/${course.id}`)
                  }
                >
                  Add Videos
                </button>
              )}

              <button
                className={course.isPublished ? "unpost" : "post"}
                onClick={() => togglePublish(course)}
              >
                {course.isPublished ? "Unpost" : "Post"}
              </button>

              <button
                className="delete"
                disabled={isPurchased}
                title={
                  isPurchased
                    ? "Students have purchased this course"
                    : "Delete course"
                }
                onClick={() => deleteCourse(course.id)}
              >
                Delete
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeacherCourse;
